import {H5WeatherForecastData, H5WeatherSource, H5WeatherVariableGroup} from 'src/lib/api/h5';
import {
  ApiForecast,
  ApiForecastTimeSeries,
  Source,
  TimeSeriesData,
  TimeSeriesVariables,
  WeatherColumns,
} from 'src/lib/api/series';

// The API's source ids are the legacy Source values, which is what the chart styles a time series
// by, so a source the chart already knows draws as soon as the API lists it. era5-land is an
// observation source with no legacy chart identity.
const CHART_SOURCES = new Set<string>(Object.values(Source));

export const chartSourceFor = (source: H5WeatherSource): Source | undefined =>
  CHART_SOURCES.has(source) ? (source as Source) : undefined;

const WEATHER_COLUMN_BY_GROUP: Record<H5WeatherVariableGroup, WeatherColumns> = {
  temperature: WeatherColumns.TEMPERATURE,
  precipitation: WeatherColumns.PRECIPITATION,
};

/**
 * An H5 weather response as the time series type the dashboard chart draws, so an H5 section
 * renders weather through the same chart as a legacy one. Undefined when the chart has no style
 * for the response's source.
 */
export function toWeatherForecastTimeSeries(
  data: H5WeatherForecastData,
  {siteId}: {siteId: string},
  reportUnsupported: (message: string) => void = console.warn
): ApiForecastTimeSeries | undefined {
  const source = chartSourceFor(data.source);
  if (source === undefined) {
    reportUnsupported(`Weather source ${data.source} has no chart style`);
    return undefined;
  }

  const framesWithData = data.forecasts.filter((frame) => frame.validTimes.length > 0);
  const columnByVariable = new Map<string, WeatherColumns>();
  const variables: TimeSeriesVariables = {};
  for (const variable of framesWithData[0]?.variables ?? []) {
    const column = WEATHER_COLUMN_BY_GROUP[variable.group];
    if (variables[column]) {
      reportUnsupported(`Weather variable ${variable.name} has no chart column of its own`);
      continue;
    }
    columnByVariable.set(variable.name, column);
    variables[column] = {unit: variable.unit};
  }

  const forecasts: ApiForecast[] = framesWithData.map((frame) => {
    const values: TimeSeriesData = {};
    for (const [name, column] of columnByVariable) {
      const series = frame.values[name];
      if (!series) {
        reportUnsupported(`Weather response omitted ${name}`);
        continue;
      }
      values[column] = series;
    }
    return {
      initializationTime: frame.initTime,
      issueTime: frame.initTime,
      validTimes: frame.validTimes,
      validTimeRange: [frame.validTimes[0], frame.validTimes[frame.validTimes.length - 1]],
      data: values,
    };
  });

  return {
    query: {
      siteId,
      source,
      columns: [...columnByVariable.values()],
      initializationTimes: forecasts.map((forecast) => forecast.initializationTime),
    },
    variables,
    forecasts,
  };
}
